import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  split,
  NormalizedCacheObject
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { RetryLink } from "@apollo/client/link/retry";
import { onError } from "@apollo/client/link/error";
import fetch from "node-fetch";
import { policies } from "@tvg/utils/apolloPolicies";
import TVGConf from "@tvg/conf";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";

export type GraphClient = "graph" | "fcp" | "rda" | "gas" | "behg";

const getClientURI = (graphClient: GraphClient): string => {
  let uri;

  switch (graphClient) {
    case "rda":
      uri = TVGConf().config().service.rda;
      break;
    default:
      uri = TVGConf().config().service.cosmo;
      break;
  }

  return uri;
};

const getClientWsUri = (graphClient: GraphClient): string | null => {
  let uri;

  switch (graphClient) {
    case "graph":
      uri = TVGConf().config().service.cosmoWS;
      break;
    default:
      uri = "";
      break;
  }

  return uri.replace("https:", "").replace("http:", "").replace("//", "wss://");
};

export default {
  getClient(
    graphClient: GraphClient = "graph"
  ): ApolloClient<NormalizedCacheObject> {
    switch (graphClient) {
      case "rda":
        // @ts-ignore
        return this.rdaClient;
      case "fcp":
        // @ts-ignore
        return this.fcpClient;
      case "gas":
        // @ts-ignore
        return this.gasClient;
      case "behg":
        // @ts-ignore
        return this.behgClient;
      default:
        // @ts-ignore
        return this.graphClient;
    }
  },

  setClient(
    client: ApolloClient<NormalizedCacheObject>,
    type: GraphClient
  ): void {
    switch (type) {
      case "rda":
        // @ts-ignore
        this.rdaClient = client;
        break;
      case "fcp":
        // @ts-ignore
        this.fcpClient = client;
        break;
      case "gas":
        // @ts-ignore
        this.gasClient = client;
        break;
      case "behg":
        // @ts-ignore
        this.behgClient = client;
        break;
      default:
        // @ts-ignore
        this.graphClient = client;
        break;
    }
  },

  createClient(
    ssrMode: boolean = false,
    graphClient: GraphClient = "graph"
  ): ApolloClient<NormalizedCacheObject> {
    const httpLink = createHttpLink({
      // @ts-ignore
      fetch: typeof window === "undefined" ? fetch : window.fetch,
      uri: getClientURI(graphClient).replace("http", "https"),
      credentials: "include",
      headers: {
        "x-tvg-context": TVGConf().context()
      }
    });

    const retryLink = new RetryLink({
      attempts: () => true,
      delay: (count: number) => {
        if (count < 25) {
          return 2000 * Math.random();
        }
        return 15000;
      }
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      if (graphQLErrors)
        graphQLErrors.map(({ message, locations, path }) =>
          // eslint-disable-next-line no-console
          console.error(
            `[GraphQL error]: Operation: ${operation.operationName}, Message: ${message}, Location: ${locations}, Path: ${path}`
          )
        );

      if (networkError) {
        console.error(`[Network error]: ${networkError}`); // eslint-disable-line no-console
      }
    });

    let link;
    // @ts-ignore
    let wsLink;
    if (typeof window === "undefined") {
      link = errorLink.concat(httpLink);
    } else {
      const wsUri = getClientWsUri(graphClient);

      if (wsUri !== "") {
        // @ts-ignore
        let activeSocket;
        // @ts-ignore
        let timedOut;
        wsLink = new GraphQLWsLink(
          createClient({
            url: wsUri || "",
            keepAlive: 20_000, // ping server every 10 seconds
            on: {
              // eslint-disable-next-line
              connected: (socket) => (activeSocket = socket),
              ping: (received) => {
                if (!received)
                  // sent
                  timedOut = setTimeout(() => {
                    // @ts-ignore
                    if (activeSocket?.readyState === WebSocket.OPEN)
                      activeSocket?.close(4408, "Request Timeout");
                  }, 5_000); // wait 5 seconds for the pong and then close the connection
              },
              pong: (received) => {
                // @ts-ignore
                if (received) clearTimeout(timedOut); // pong is received, clear connection close timeout
              }
            },
            // @ts-ignore
            error: (err) => console.log(err, "ERROR")
          })
        );
      }

      const tmplink = wsLink
        ? split(
            // split based on operation type
            ({ query }) => {
              const definition = getMainDefinition(query);
              return (
                definition.kind === "OperationDefinition" &&
                definition.operation === "subscription"
              );
            },
            wsLink,
            httpLink
          )
        : httpLink;

      link = retryLink.concat(errorLink).concat(tmplink);
    }

    const cache = new InMemoryCache(policies);

    if (typeof window !== "undefined") {
      // @ts-ignore
      cache.restore(window.__APOLLO_STATE__); // eslint-disable-line no-underscore-dangle
    }

    const client = new ApolloClient({
      link,
      cache,
      // @ts-ignore
      addTypename: true,
      connectToDevTools: true,
      ssrMode
    });

    this.setClient(client, graphClient);

    if (wsLink) {
      return {
        ...client,
        // @ts-ignore
        subscriptionCosmoClient: wsLink?.client,
        /* eslint-disable */ // @ts-ignore
        subscribe: client.__proto__.subscribe,
        // @ts-ignore
        stop: client.__proto__.stop
        /* eslint-enable */
      };
    }

    return client;
  }
};
